// Morning log reminder via @capacitor/local-notifications. iOS native only —
// all imports are dynamic with graceful web fallback: on web every function
// resolves to 'unavailable' / false and the UI hides the reminder surfaces.
//
// Permission is requested contextually when the user enables the toggle
// (onboarding step 3 or Settings > Reminders) — never at app launch.
//
// ⚠️ THENABLE-PROXY TRAP: a Capacitor plugin handle is a JS Proxy that
// intercepts EVERY property get — including `then` — so awaiting it (or
// returning it from an async function) hangs the caller forever. Async helpers
// here wrap the proxy in { api }; never return the bare proxy.

import { isNative } from '../utils/platform';

// Single well-known id — the schedule is idempotent: we always cancel this id
// before rescheduling, so there is never more than one pending morning reminder.
export const MORNING_REMINDER_ID = 806001;
export const DEFAULT_REMINDER_TIME = '08:00';

// Resolves { api }, or null on web / when the plugin can't load.
async function plugin() {
  if (!isNative()) return null;
  try {
    const mod = await import('@capacitor/local-notifications');
    return { api: mod.LocalNotifications };
  } catch {
    return null;
  }
}

// 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'unavailable'
export async function getReminderPermissionState() {
  const ln = (await plugin())?.api;
  if (!ln) return 'unavailable';
  try {
    const res = await ln.checkPermissions();
    return res?.display || 'unavailable';
  } catch {
    return 'unavailable';
  }
}

// Shows the iOS permission sheet (or resolves immediately if already decided).
export async function requestReminderPermission() {
  const ln = (await plugin())?.api;
  if (!ln) return 'unavailable';
  try {
    const res = await ln.requestPermissions();
    return res?.display || 'denied';
  } catch {
    return 'denied';
  }
}

// Notification copy varies by streak so the reminder earns its tap.
export function reminderCopy(streakCount) {
  if (streakCount >= 2) {
    return {
      title: 'Good morning ☀️',
      body: `Log last night to keep your ${streakCount}-day streak alive`,
    };
  }
  return {
    title: 'Good morning ☀️',
    body: 'How did you sleep? Log it in 30 seconds.',
  };
}

// Daily repeating reminder at `time` (HH:MM). Idempotent — cancels the known
// id first, so calling on every app open just re-asserts the schedule (and
// refreshes the streak copy). Returns true when scheduled.
export async function scheduleMorningReminder({ time = DEFAULT_REMINDER_TIME, streakCount = 0 } = {}) {
  const ln = (await plugin())?.api;
  if (!ln) return false;
  const [hour, minute] = String(time).split(':').map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return false;
  const { title, body } = reminderCopy(streakCount);
  try {
    try {
      await ln.cancel({ notifications: [{ id: MORNING_REMINDER_ID }] });
    } catch { /* nothing pending — fine */ }
    await ln.schedule({
      notifications: [{
        id: MORNING_REMINDER_ID,
        title,
        body,
        schedule: { on: { hour, minute }, allowWhileIdle: true },
        extra: { route: '/log' },
      }],
    });
    return true;
  } catch (err) {
    console.warn('Failed to schedule morning reminder:', err);
    return false;
  }
}

export async function cancelMorningReminder() {
  const ln = (await plugin())?.api;
  if (!ln) return;
  try {
    await ln.cancel({ notifications: [{ id: MORNING_REMINDER_ID }] });
  } catch { /* nothing pending — fine */ }
}

// Deep link: tapping the reminder opens the app on /log. Registered exactly
// once for the app lifetime (module-level guard) — NOT re-bound per render.
let tapListenerRegistered = false;
export async function registerReminderTapListener(onTap) {
  if (tapListenerRegistered) return;
  const ln = (await plugin())?.api;
  if (!ln) return;
  tapListenerRegistered = true;
  ln.addListener('localNotificationActionPerformed', (event) => {
    try {
      if (Number(event?.notification?.id) !== MORNING_REMINDER_ID) return;
      onTap(event?.notification?.extra?.route || '/log');
    } catch (err) {
      console.warn('Reminder tap handler failed:', err);
    }
  });
}
